import { useState, type ReactNode } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import type { TFunction } from "i18next";
import { Trans, useTranslation } from "react-i18next";
import { match } from "ts-pattern";
import type { DistributedOmit, Promisable } from "type-fest";

import {
  CALENDAR_TIME_ZONE,
  DateOfBirthSchema,
  GuestAccessCodeSchema,
  type DateOfBirth,
  type GuestAccessLinkOpened,
  type GuestAccessLinkState,
} from "@thalamos/haven-common";

import { CancelOrProceedActions } from "../../components/CancelOrProceedActions/CancelOrProceedActions.js";
import { tk, type TranslationKey } from "../../locales/translationKeys.js";
import { VISUALLY_HIDDEN } from "../../theme/visuallyHidden.js";
import { GuestBrand } from "./GuestBrand.js";

/** The link as opened: who it is from, and about whom. */
type OpenedLink = DistributedOmit<GuestAccessLinkOpened, "state">;

// The end of a UK calendar day, so it is shown as a UK date wherever the
// browser is.
const UK_CALENDAR_DATE = new Intl.DateTimeFormat("en-GB", {
  dateStyle: "long",
  timeZone: CALENDAR_TIME_ZONE,
});

/** What went wrong with the last code tried, if anything. */
export type CodeProblem =
  | { kind: "wrong"; triesLeft: number }
  | { kind: "expired" }
  | { kind: "sent_a_moment_ago" };

/**
 * Where signing in through a link stands, which is all the page shows.
 *
 * - `opening`: the link is being opened.
 * - `no_link`: the address holds no link, or one nobody issued.
 * - `unavailable`: the server could not be reached; trying again may work.
 * - `closed`: the link no longer opens, and why.
 * - `declined`: the guest has just said the link was not meant for them.
 * - `welcome`, `code`, `date_of_birth`, `agreement`: the steps of signing in.
 */
export type GuestSignIn =
  | { kind: "opening" }
  | { kind: "no_link" }
  | { kind: "unavailable" }
  | {
      kind: "closed";
      state: Exclude<GuestAccessLinkState, "open" | "declined">;
      link: OpenedLink;
    }
  | { kind: "declined"; link: OpenedLink }
  | { kind: "welcome"; link: OpenedLink }
  | { kind: "code"; link: OpenedLink; problem?: CodeProblem | undefined }
  | {
      kind: "date_of_birth";
      link: OpenedLink;
      /** Tries left after a wrong date; absent before one. */
      triesLeft?: number | undefined;
    }
  | {
      kind: "agreement";
      link: OpenedLink;
      wording: { text: string; digest: string };
    };

/** What the guest can do at each step. Each settles once the page has moved on. */
export type GuestSignInActions = {
  onSendCode: () => Promisable<void>;
  onEnterCode: (code: string) => Promisable<void>;
  onEnterDateOfBirth: (dateOfBirth: DateOfBirth) => Promisable<void>;
  onAcceptAgreement: (wordingDigest: string) => Promisable<void>;
  onDecline: () => Promisable<void>;
  onRetry: () => Promisable<void>;
};

/**
 * A page of its own for each step: Haven's mark over a card holding a heading
 * and what the step asks.
 */
const GuestPage = ({
  heading,
  children,
}: {
  heading: string;
  children: ReactNode;
}) => (
  <Box
    sx={{
      minHeight: "100vh",
      display: "flex",
      flexDirection: "column",
      bgcolor: "grey.100",
      px: { xs: 1.5, sm: 3 },
      py: { xs: 3, sm: 6 },
    }}
  >
    <Stack
      spacing={2}
      alignSelf="center"
      sx={{ flex: 1, justifyContent: "center", maxWidth: 560, width: "100%" }}
    >
      <Box sx={{ display: "flex", justifyContent: "center" }}>
        <GuestBrand />
      </Box>
      <Paper variant="outlined" sx={{ borderRadius: 2, p: { xs: 2, sm: 3 } }}>
        <Stack spacing={3}>
          <Typography variant="h5" component="h1">
            {heading}
          </Typography>
          {children}
        </Stack>
      </Paper>
    </Stack>
  </Box>
);

/**
 * Why a link will not open, in a heading. An expired link names the day it
 * ended; one with no end set cannot have expired, so says only that it ended.
 */
const closedHeading = ({
  state,
  link,
  t,
}: {
  state: Extract<GuestSignIn, { kind: "closed" }>["state"];
  link: OpenedLink;
  t: TFunction;
}): string => {
  if (state !== "expired") {
    return t(`guestAccess.closed.${state}.heading`);
  }

  return link.expiresAt === null
    ? t("guestAccess.closed.ended.heading")
    : t("guestAccess.closed.expired.heading", {
        date: UK_CALENDAR_DATE.format(link.expiresAt),
      });
};

/** Who to ask about a link that will not open. */
const AskTheSharer = ({ link }: { link: OpenedLink }) => {
  const { t } = useTranslation();

  return (
    <Typography>
      {t("guestAccess.closed.contact", {
        sharer: link.sharerName,
        organisation: link.organisationName,
      })}
    </Typography>
  );
};

/**
 * "This wasn't meant for me", offered at every step before the guest is in.
 * Ending the link cannot be undone, so it is asked about before `onDecline`.
 */
const Decline = ({
  link,
  onDecline,
  disabled,
  initiallyConfirming,
}: {
  link: OpenedLink;
  onDecline: () => Promisable<void>;
  disabled: boolean;
  initiallyConfirming: boolean;
}) => {
  const { t } = useTranslation();
  const [confirming, setConfirming] = useState(initiallyConfirming);
  const cancel = () => {
    setConfirming(false);
  };

  return (
    <Box sx={{ textAlign: "center" }}>
      <Button
        variant="outlined"
        disabled={disabled}
        onClick={() => {
          setConfirming(true);
        }}
      >
        {t("guestAccess.decline.offer")}
      </Button>
      <Dialog open={confirming} onClose={cancel} maxWidth="xs" fullWidth>
        <DialogTitle>{t("guestAccess.decline.title")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("guestAccess.decline.body", { sharer: link.sharerName })}
          </DialogContentText>
        </DialogContent>
        <CancelOrProceedActions
          cancel={{ onClick: cancel }}
          proceed={{
            label: t("guestAccess.decline.end"),
            kind: "destructive",
            onClick: () => {
              setConfirming(false);
              return onDecline();
            },
          }}
        />
      </Dialog>
    </Box>
  );
};

/** Runs one action at a time, so a button cannot send its request twice. */
const useOneAtATime = () => {
  const [busy, setBusy] = useState(false);
  const run = async (action: () => Promisable<void>) => {
    setBusy(true);
    try {
      await action();
    } finally {
      setBusy(false);
    }
  };

  return { busy, run };
};

type StepProps = {
  link: OpenedLink;
  actions: GuestSignInActions;
  initiallyConfirmingDecline: boolean;
};

const CodeStep = ({
  link,
  problem,
  actions,
  initiallyConfirmingDecline,
}: StepProps & { problem: CodeProblem | undefined }) => {
  const { t } = useTranslation();
  const [code, setCode] = useState("");
  const [tried, setTried] = useState(false);
  const { busy, run } = useOneAtATime();
  const wellFormed = GuestAccessCodeSchema.safeParse(code).success;

  const message =
    tried && !wellFormed
      ? t("guestAccess.code.sixDigits")
      : problem === undefined
        ? undefined
        : match(problem)
            .with({ kind: "wrong" }, ({ triesLeft }) =>
              t(
                triesLeft === 1
                  ? "guestAccess.code.wrongLastTry"
                  : "guestAccess.code.wrong",
                { count: triesLeft },
              ),
            )
            .with({ kind: "expired" }, () => t("guestAccess.code.expired"))
            .with({ kind: "sent_a_moment_ago" }, () =>
              t("guestAccess.code.sentAMomentAgo"),
            )
            .exhaustive();

  return (
    <GuestPage heading={t("guestAccess.code.heading")}>
      <Typography>{t("guestAccess.code.intro")}</Typography>
      <TextField
        label={t("guestAccess.code.label")}
        value={code}
        disabled={busy}
        error={message !== undefined && problem?.kind !== "sent_a_moment_ago"}
        helperText={message}
        onChange={(event) => {
          setCode(event.target.value.replace(/\s/g, ""));
        }}
        slotProps={{
          htmlInput: {
            inputMode: "numeric",
            autoComplete: "one-time-code",
          },
        }}
        sx={{ maxWidth: 200 }}
      />
      <Stack direction="row" spacing={2}>
        <Button
          variant="contained"
          disabled={busy}
          onClick={() => {
            setTried(true);
            if (wellFormed) {
              void run(() => actions.onEnterCode(code));
            }
          }}
        >
          {t("guestAccess.continue")}
        </Button>
        <Button
          disabled={busy}
          onClick={() => {
            setCode("");
            setTried(false);
            void run(actions.onSendCode);
          }}
        >
          {t("guestAccess.code.sendAgain")}
        </Button>
      </Stack>
      <Decline
        link={link}
        onDecline={() => run(actions.onDecline)}
        disabled={busy}
        initiallyConfirming={initiallyConfirmingDecline}
      />
      <AskTheSharer link={link} />
    </GuestPage>
  );
};

const DateOfBirthStep = ({
  link,
  triesLeft,
  actions,
  initiallyConfirmingDecline,
}: StepProps & { triesLeft: number | undefined }) => {
  const { t } = useTranslation();
  const [day, setDay] = useState("");
  const [month, setMonth] = useState("");
  const [year, setYear] = useState("");
  const [tried, setTried] = useState(false);
  const { busy, run } = useOneAtATime();
  const parsed = DateOfBirthSchema.safeParse(
    `${year.padStart(4, "0")}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`,
  );

  const message =
    tried && !parsed.success
      ? t("guestAccess.dateOfBirth.invalid")
      : triesLeft === undefined
        ? undefined
        : t(
            triesLeft === 1
              ? "guestAccess.dateOfBirth.wrongLastTry"
              : "guestAccess.dateOfBirth.wrong",
            { count: triesLeft },
          );
  const field = (
    label: string,
    value: string,
    onChange: (value: string) => void,
    width: number,
  ) => (
    <TextField
      label={label}
      value={value}
      disabled={busy}
      error={message !== undefined}
      onChange={(event) => {
        onChange(event.target.value.trim());
      }}
      slotProps={{ htmlInput: { inputMode: "numeric" } }}
      sx={{ width }}
    />
  );

  return (
    <GuestPage
      heading={t("guestAccess.dateOfBirth.heading", {
        personFirstName: link.personFirstName,
      })}
    >
      <Typography>{t("guestAccess.dateOfBirth.intro")}</Typography>
      <Stack
        component="fieldset"
        direction="row"
        spacing={2}
        sx={{ border: 0, p: 0, m: 0 }}
        aria-describedby={
          message === undefined ? undefined : "guest-dob-message"
        }
      >
        <Box component="legend" sx={VISUALLY_HIDDEN}>
          {t("guestAccess.dateOfBirth.heading", {
            personFirstName: link.personFirstName,
          })}
        </Box>
        {field(t("guestAccess.dateOfBirth.day"), day, setDay, 80)}
        {field(t("guestAccess.dateOfBirth.month"), month, setMonth, 80)}
        {field(t("guestAccess.dateOfBirth.year"), year, setYear, 110)}
      </Stack>
      {message === undefined ? null : (
        <Typography id="guest-dob-message" color="error" variant="body2">
          {message}
        </Typography>
      )}
      <Box>
        <Button
          variant="contained"
          disabled={busy}
          onClick={() => {
            setTried(true);
            if (parsed.success) {
              void run(() => actions.onEnterDateOfBirth(parsed.data));
            }
          }}
        >
          {t("guestAccess.continue")}
        </Button>
      </Box>
      <Decline
        link={link}
        onDecline={() => run(actions.onDecline)}
        disabled={busy}
        initiallyConfirming={initiallyConfirmingDecline}
      />
      <AskTheSharer link={link} />
    </GuestPage>
  );
};

const AgreementStep = ({
  link,
  wording,
  actions,
  initiallyConfirmingDecline,
}: StepProps & { wording: { text: string; digest: string } }) => {
  const { t } = useTranslation();
  const [agreed, setAgreed] = useState(false);
  const { busy, run } = useOneAtATime();

  return (
    <GuestPage heading={t("guestAccess.agreement.heading")}>
      <Typography sx={{ whiteSpace: "pre-line" }}>{wording.text}</Typography>
      <FormControlLabel
        control={
          <Checkbox
            checked={agreed}
            disabled={busy}
            onChange={(event) => {
              setAgreed(event.target.checked);
            }}
          />
        }
        label={t("guestAccess.agreement.agree")}
      />
      <Box>
        <Button
          variant="contained"
          disabled={busy || !agreed}
          onClick={() => {
            void run(() => actions.onAcceptAgreement(wording.digest));
          }}
        >
          {t("guestAccess.continue")}
        </Button>
      </Box>
      <Decline
        link={link}
        onDecline={() => run(actions.onDecline)}
        disabled={busy}
        initiallyConfirming={initiallyConfirmingDecline}
      />
    </GuestPage>
  );
};

/**
 * What the welcome says for the link's purpose: who shared or asked, and what
 * follows sending the code. The privacy line is a key, because its address is
 * set in bold.
 */
const welcomeWordsFor = ({
  link,
  t,
}: {
  link: OpenedLink;
  t: TFunction;
}): {
  heading: string;
  body: string;
  privacyKey: TranslationKey;
  closing: string | undefined;
} => {
  const endsOn =
    link.expiresAt === null ? null : UK_CALENDAR_DATE.format(link.expiresAt);
  const named = {
    guest: link.guestName,
    sharer: link.sharerName,
    organisation: link.organisationName,
    personFirstName: link.personFirstName,
  };

  return match(link)
    .with(
      { purpose: "read_shared_assessments" },
      ({ sharedAssessmentCount }) => ({
        heading: t("guestAccess.welcome.read_shared_assessments.heading", {
          ...named,
          count: sharedAssessmentCount,
        }),
        body: t("guestAccess.welcome.read_shared_assessments.body", {
          ...named,
          count: sharedAssessmentCount,
        }),
        privacyKey: tk("guestAccess.welcome.read_shared_assessments.private"),
        closing:
          endsOn === null
            ? undefined
            : t("guestAccess.welcome.read_shared_assessments.until", {
                date: endsOn,
              }),
      }),
    )
    .with({ purpose: "complete_assessment" }, () => ({
      heading: t("guestAccess.welcome.complete_assessment.heading", named),
      body: t("guestAccess.welcome.complete_assessment.body", named),
      privacyKey: tk("guestAccess.welcome.complete_assessment.private"),
      closing:
        endsOn === null
          ? t("guestAccess.welcome.complete_assessment.answersSave")
          : t("guestAccess.welcome.complete_assessment.sendBy", {
              date: endsOn,
            }),
    }))
    .exhaustive();
};

const WelcomeStep = ({
  link,
  actions,
  initiallyConfirmingDecline,
}: StepProps) => {
  const { t } = useTranslation();
  const { busy, run } = useOneAtATime();
  const words = welcomeWordsFor({ link, t });

  return (
    <GuestPage heading={words.heading}>
      <Typography>{words.body}</Typography>
      <Typography>
        <Trans
          i18nKey={words.privacyKey}
          values={{ maskedEmailAddress: link.maskedEmailAddress }}
          components={{ strong: <strong /> }}
        />
      </Typography>
      <Box sx={{ textAlign: "center" }}>
        <Button
          variant="contained"
          disabled={busy}
          onClick={() => {
            void run(actions.onSendCode);
          }}
        >
          {t("guestAccess.welcome.sendCode")}
        </Button>
      </Box>
      {words.closing === undefined ? null : (
        <Typography color="text.secondary" align="center">
          {words.closing}
        </Typography>
      )}
      <Decline
        link={link}
        onDecline={() => run(actions.onDecline)}
        disabled={busy}
        initiallyConfirming={initiallyConfirmingDecline}
      />
    </GuestPage>
  );
};

/**
 * Signing in through a guest access link, one step to a page: the link, a
 * code emailed to its address, the person's date of birth, and the
 * information governance wording where it has not been accepted. Each state
 * a link can be in that stops it opening has a page of its own saying why.
 */
export const GuestSignInContent = ({
  signIn,
  actions,
  initiallyConfirmingDecline = false,
}: {
  signIn: GuestSignIn;
  actions: GuestSignInActions;
  /**
   * Opens a step with the decline already being confirmed, for a story to
   * show; leave unset otherwise.
   */
  initiallyConfirmingDecline?: boolean;
}) => {
  const { t } = useTranslation();

  return match(signIn)
    .with({ kind: "opening" }, () => (
      <GuestPage heading={t("guestAccess.opening")}>
        <Typography color="text.secondary">{t("common.loading")}</Typography>
      </GuestPage>
    ))
    .with({ kind: "no_link" }, () => (
      <GuestPage heading={t("guestAccess.noLink.heading")}>
        <Typography>{t("guestAccess.noLink.body")}</Typography>
      </GuestPage>
    ))
    .with({ kind: "unavailable" }, () => (
      <GuestPage heading={t("guestAccess.unavailable.heading")}>
        <Alert severity="error">{t("guestAccess.unavailable.body")}</Alert>
        <Box>
          <Button
            variant="contained"
            onClick={() => {
              void actions.onRetry();
            }}
          >
            {t("guestAccess.unavailable.retry")}
          </Button>
        </Box>
      </GuestPage>
    ))
    .with({ kind: "closed" }, ({ state, link }) => (
      <GuestPage heading={closedHeading({ state, link, t })}>
        <Typography>
          {t(`guestAccess.closed.${state}.body`, {
            sharer: link.sharerName,
            organisation: link.organisationName,
          })}
        </Typography>
        <AskTheSharer link={link} />
      </GuestPage>
    ))
    .with({ kind: "declined" }, ({ link }) => (
      <GuestPage heading={t("guestAccess.declined.heading")}>
        <Typography>
          {t("guestAccess.declined.body", { sharer: link.sharerName })}
        </Typography>
      </GuestPage>
    ))
    .with({ kind: "welcome" }, ({ link }) => (
      <WelcomeStep
        link={link}
        actions={actions}
        initiallyConfirmingDecline={initiallyConfirmingDecline}
      />
    ))
    .with({ kind: "code" }, ({ link, problem }) => (
      <CodeStep
        link={link}
        problem={problem}
        actions={actions}
        initiallyConfirmingDecline={initiallyConfirmingDecline}
      />
    ))
    .with({ kind: "date_of_birth" }, ({ link, triesLeft }) => (
      <DateOfBirthStep
        link={link}
        triesLeft={triesLeft}
        actions={actions}
        initiallyConfirmingDecline={initiallyConfirmingDecline}
      />
    ))
    .with({ kind: "agreement" }, ({ link, wording }) => (
      <AgreementStep
        link={link}
        wording={wording}
        actions={actions}
        initiallyConfirmingDecline={initiallyConfirmingDecline}
      />
    ))
    .exhaustive();
};
